import {assert} from 'Asserts';

import type {FlowchartEdgeSpec, MethodLink} from './ProcedureRunDesignFlowchartLayoutUtils';

export interface ReplicateFlowchartAutomappingNode {
  nodeId: string;
  isSourceEligible: boolean;
  isTargetEligible: boolean;
}

interface EdgeEndpoints {
  sourceNodeId: string;
  targetNodeId: string;
}

interface GetMissingReplicateFlowchartEdgeSpecsArgs {
  directMethodLinks: ReadonlyArray<MethodLink>;
  nodesByMethodId: ReadonlyMap<string, ReadonlyArray<ReplicateFlowchartAutomappingNode>>;
  existingEdgeSpecs: ReadonlyArray<EdgeEndpoints>;
}

/** Existing per-node edge counts for the source and target sides of one direct method pair. */
interface MethodPairEdgeCounts {
  numEdgesBySourceNodeId: Map<string, number>;
  numEdgesByTargetNodeId: Map<string, number>;
}

/** Records a directed edge in the source-to-target adjacency map. */
function addEdgeToAdjacencyMap(
  targetsBySourceNodeId: Map<string, Set<string>>,
  sourceNodeId: string,
  targetNodeId: string
): void {
  const targetNodeIds = targetsBySourceNodeId.get(sourceNodeId) ?? new Set<string>();
  targetNodeIds.add(targetNodeId);
  targetsBySourceNodeId.set(sourceNodeId, targetNodeIds);
}

/**
 * Calculates the total vertical distance for assigning ordered nodes to contiguous
 * candidate blocks. The hypothetical additional assignment is used to compare tied quota choices.
 */
function getAssignmentPositionDistance(
  assignmentNodeIds: ReadonlyArray<string>,
  candidateNodeIds: ReadonlyArray<string>,
  numAssignmentsByCandidateNodeId: ReadonlyMap<string, number>,
  assignmentPositionsByNodeId: ReadonlyMap<string, number>,
  candidatePositionsByNodeId: ReadonlyMap<string, number>,
  candidateNodeIdForAdditionalAssignment: string | null
): number {
  let assignmentIndex = 0;
  let totalDistance = 0;
  candidateNodeIds.forEach((candidateNodeId) => {
    const candidatePosition = candidatePositionsByNodeId.get(candidateNodeId);
    assert(candidatePosition != null, `Expected candidate node ${candidateNodeId} to have a position`);
    const existingNumAssignments = numAssignmentsByCandidateNodeId.get(candidateNodeId);
    assert(
      existingNumAssignments != null,
      `Expected candidate node ${candidateNodeId} to have an assignment count`
    );
    const numAssignments =
      existingNumAssignments + (candidateNodeId === candidateNodeIdForAdditionalAssignment ? 1 : 0);
    for (let index = 0; index < numAssignments; index++) {
      const assignmentNodeId = assignmentNodeIds[assignmentIndex];
      assert(assignmentNodeId != null, 'Expected enough nodes for the candidate assignments');
      const assignmentPosition = assignmentPositionsByNodeId.get(assignmentNodeId);
      assert(assignmentPosition != null, `Expected assignment node ${assignmentNodeId} to have a position`);
      totalDistance += Math.abs(candidatePosition - assignmentPosition);
      assignmentIndex += 1;
    }
  });
  return totalDistance;
}

/**
 * Returns balanced assignment quotas for candidates, using vertical distance to resolve
 * equal-degree choices. Every uncovered candidate receives at least one assignment.
 *
 * @param assignmentNodeIds - Uncovered nodes on the larger side, in vertical layout order.
 * @param candidateNodeIds - Eligible nodes on the opposite side, in vertical layout order.
 * @param uncoveredCandidateNodeIds - Candidates that must receive at least one assignment.
 * @param numExistingEdgesByCandidateNodeId - Existing edge count for each candidate.
 * @param assignmentPositionsByNodeId - Vertical layout position of each assignment node.
 * @param candidatePositionsByNodeId - Vertical layout position of each candidate node.
 */
function getBalancedAssignmentCountsByNodeId(
  assignmentNodeIds: ReadonlyArray<string>,
  candidateNodeIds: ReadonlyArray<string>,
  uncoveredCandidateNodeIds: ReadonlyArray<string>,
  numExistingEdgesByCandidateNodeId: ReadonlyMap<string, number>,
  assignmentPositionsByNodeId: ReadonlyMap<string, number>,
  candidatePositionsByNodeId: ReadonlyMap<string, number>
): Map<string, number> {
  assert(
    assignmentNodeIds.length >= uncoveredCandidateNodeIds.length,
    'Expected the assignment side to cover every uncovered candidate'
  );
  const uncoveredCandidateNodeIdSet = new Set(uncoveredCandidateNodeIds);
  // Tracks the number of new edges to add by candidate node ID
  const numAssignmentsByCandidateNodeId = new Map(
    candidateNodeIds.map((nodeId) => [nodeId, uncoveredCandidateNodeIdSet.has(nodeId) ? 1 : 0])
  );
  let numAssignedNodes = uncoveredCandidateNodeIds.length;

  while (numAssignedNodes < assignmentNodeIds.length) {
    let bestCandidateNodeId: string | null = null;
    let bestDegree = Number.POSITIVE_INFINITY;
    let bestPositionDistance = Number.POSITIVE_INFINITY;
    candidateNodeIds.forEach((candidateNodeId) => {
      const numExistingEdges = numExistingEdgesByCandidateNodeId.get(candidateNodeId);
      const numAssignments = numAssignmentsByCandidateNodeId.get(candidateNodeId);
      assert(numExistingEdges != null, `Expected candidate node ${candidateNodeId} to have an edge count`);
      assert(
        numAssignments != null,
        `Expected candidate node ${candidateNodeId} to have an assignment count`
      );
      const degree = numExistingEdges + numAssignments;
      if (degree > bestDegree) {
        return;
      }
      const positionDistance = getAssignmentPositionDistance(
        assignmentNodeIds,
        candidateNodeIds,
        numAssignmentsByCandidateNodeId,
        assignmentPositionsByNodeId,
        candidatePositionsByNodeId,
        candidateNodeId
      );
      if (degree < bestDegree || positionDistance < bestPositionDistance) {
        bestCandidateNodeId = candidateNodeId;
        bestDegree = degree;
        bestPositionDistance = positionDistance;
      }
    });

    assert(bestCandidateNodeId != null, 'Expected an eligible candidate node');
    const numAssignments = numAssignmentsByCandidateNodeId.get(bestCandidateNodeId);
    assert(
      numAssignments != null,
      `Expected candidate node ${bestCandidateNodeId} to have an assignment count`
    );
    numAssignmentsByCandidateNodeId.set(bestCandidateNodeId, numAssignments + 1);
    numAssignedNodes += 1;
  }
  return numAssignmentsByCandidateNodeId;
}

function buildAdjacencyMap(edgeSpecs: ReadonlyArray<EdgeEndpoints>): Map<string, Set<string>> {
  const targetsBySourceNodeId = new Map<string, Set<string>>();
  edgeSpecs.forEach(({sourceNodeId, targetNodeId}) => {
    addEdgeToAdjacencyMap(targetsBySourceNodeId, sourceNodeId, targetNodeId);
  });
  return targetsBySourceNodeId;
}

/**
 * Counts existing outgoing and incoming edges for eligible nodes in one direct method pair.
 *
 * @param sourceNodeIds - All node IDs in the source method, in vertical layout order.
 * @param targetNodeIds - All node IDs in the target method, in vertical layout order.
 * @param eligibleSourceNodeIds - Source-method node IDs allowed to receive new outgoing edges.
 * @param eligibleTargetNodeIds - Target-method node IDs allowed to receive new incoming edges.
 * @param targetsBySourceNodeId - Adjacency map containing current edges across the full flowchart.
 */
function getEdgeCountsForAdjacentMethodPair(
  sourceNodeIds: ReadonlyArray<string>,
  targetNodeIds: ReadonlyArray<string>,
  eligibleSourceNodeIds: ReadonlyArray<string>,
  eligibleTargetNodeIds: ReadonlyArray<string>,
  targetsBySourceNodeId: ReadonlyMap<string, ReadonlySet<string>>
): MethodPairEdgeCounts {
  const sourceNodeIdSet = new Set(sourceNodeIds);
  const targetNodeIdSet = new Set(targetNodeIds);
  const numEdgesBySourceNodeId = new Map(eligibleSourceNodeIds.map((nodeId) => [nodeId, 0]));
  const numEdgesByTargetNodeId = new Map(eligibleTargetNodeIds.map((nodeId) => [nodeId, 0]));

  targetsBySourceNodeId.forEach((existingTargetNodeIds, sourceNodeId) => {
    // Source is not in the method pair
    if (!sourceNodeIdSet.has(sourceNodeId)) {
      return;
    }
    existingTargetNodeIds.forEach((targetNodeId) => {
      assert(
        targetNodeIdSet.has(targetNodeId),
        `Expected source node ${sourceNodeId} to have edges only to the target method`
      );
      const sourceEdgeCount = numEdgesBySourceNodeId.get(sourceNodeId);
      // Skip adding to the map if the source node is not an eligible source
      if (sourceEdgeCount != null) {
        numEdgesBySourceNodeId.set(sourceNodeId, sourceEdgeCount + 1);
      }
      const targetEdgeCount = numEdgesByTargetNodeId.get(targetNodeId);
      // Skip adding to the map if the target node is not an eligible target
      if (targetEdgeCount != null) {
        numEdgesByTargetNodeId.set(targetNodeId, targetEdgeCount + 1);
      }
    });
  });
  return {numEdgesBySourceNodeId, numEdgesByTargetNodeId};
}

/** Records a generated edge and updates the adjacency map and endpoint edge counts. */
function recordMissingEdge(
  edgeSpec: FlowchartEdgeSpec,
  missingEdgeSpecs: Array<FlowchartEdgeSpec>,
  targetsBySourceNodeId: Map<string, Set<string>>,
  numEdgesBySourceNodeId: Map<string, number>,
  numEdgesByTargetNodeId: Map<string, number>
): void {
  missingEdgeSpecs.push(edgeSpec);
  addEdgeToAdjacencyMap(targetsBySourceNodeId, edgeSpec.sourceNodeId, edgeSpec.targetNodeId);
  numEdgesBySourceNodeId.set(
    edgeSpec.sourceNodeId,
    (numEdgesBySourceNodeId.get(edgeSpec.sourceNodeId) ?? 0) + 1
  );
  numEdgesByTargetNodeId.set(
    edgeSpec.targetNodeId,
    (numEdgesByTargetNodeId.get(edgeSpec.targetNodeId) ?? 0) + 1
  );
}

/** Records assignments as contiguous candidate blocks so generated edges do not cross. */
function recordOrderedAssignments(
  assignmentNodeIds: ReadonlyArray<string>,
  candidateNodeIds: ReadonlyArray<string>,
  numAssignmentsByCandidateNodeId: ReadonlyMap<string, number>,
  getEdgeSpec: (assignmentNodeId: string, candidateNodeId: string) => FlowchartEdgeSpec,
  missingEdgeSpecs: Array<FlowchartEdgeSpec>,
  targetsBySourceNodeId: Map<string, Set<string>>,
  numEdgesBySourceNodeId: Map<string, number>,
  numEdgesByTargetNodeId: Map<string, number>
): void {
  let assignmentIndex = 0;
  candidateNodeIds.forEach((candidateNodeId) => {
    const numAssignments = numAssignmentsByCandidateNodeId.get(candidateNodeId);
    assert(numAssignments != null, `Expected candidate node ${candidateNodeId} to have an assignment count`);
    for (let index = 0; index < numAssignments; index++) {
      const assignmentNodeId = assignmentNodeIds[assignmentIndex];
      assert(assignmentNodeId != null, 'Expected enough nodes for the candidate assignments');
      recordMissingEdge(
        getEdgeSpec(assignmentNodeId, candidateNodeId),
        missingEdgeSpecs,
        targetsBySourceNodeId,
        numEdgesBySourceNodeId,
        numEdgesByTargetNodeId
      );
      assignmentIndex += 1;
    }
  });
  assert(assignmentIndex === assignmentNodeIds.length, 'Expected every node to be assigned');
}

/** Returns and records the minimum missing edges for one direct method pair. */
function getMissingEdgeSpecsForMethodPair(
  sourceNodes: ReadonlyArray<ReplicateFlowchartAutomappingNode>,
  targetNodes: ReadonlyArray<ReplicateFlowchartAutomappingNode>,
  targetsBySourceNodeId: Map<string, Set<string>>
): Array<FlowchartEdgeSpec> {
  const sourceNodeIds = sourceNodes.map(({nodeId}) => nodeId);
  const targetNodeIds = targetNodes.map(({nodeId}) => nodeId);
  const eligibleSourceNodeIds = sourceNodes
    .filter(({isSourceEligible}) => isSourceEligible)
    .map(({nodeId}) => nodeId);
  const eligibleTargetNodeIds = targetNodes
    .filter(({isTargetEligible}) => isTargetEligible)
    .map(({nodeId}) => nodeId);
  if (eligibleSourceNodeIds.length === 0 || eligibleTargetNodeIds.length === 0) {
    return [];
  }

  const {numEdgesBySourceNodeId, numEdgesByTargetNodeId} = getEdgeCountsForAdjacentMethodPair(
    sourceNodeIds,
    targetNodeIds,
    eligibleSourceNodeIds,
    eligibleTargetNodeIds,
    targetsBySourceNodeId
  );
  const uncoveredSourceNodeIds = eligibleSourceNodeIds.filter(
    (nodeId) => numEdgesBySourceNodeId.get(nodeId) === 0
  );
  const uncoveredTargetNodeIds = eligibleTargetNodeIds.filter(
    (nodeId) => numEdgesByTargetNodeId.get(nodeId) === 0
  );
  const sourcePositionsByNodeId = new Map(sourceNodeIds.map((nodeId, position) => [nodeId, position]));
  const targetPositionsByNodeId = new Map(targetNodeIds.map((nodeId, position) => [nodeId, position]));
  const missingEdgeSpecs: Array<FlowchartEdgeSpec> = [];

  // Use the larger uncovered side as the assignment side so every uncovered node gets an edge in the
  // minimum max(uncovered sources, uncovered targets) additions. Balanced candidate quotas cover the
  // smaller side, and assigning each quota as a contiguous vertical block avoids crossing new edges.
  if (uncoveredTargetNodeIds.length >= uncoveredSourceNodeIds.length) {
    const numAssignmentsBySourceNodeId = getBalancedAssignmentCountsByNodeId(
      uncoveredTargetNodeIds,
      eligibleSourceNodeIds,
      uncoveredSourceNodeIds,
      numEdgesBySourceNodeId,
      targetPositionsByNodeId,
      sourcePositionsByNodeId
    );
    recordOrderedAssignments(
      uncoveredTargetNodeIds,
      eligibleSourceNodeIds,
      numAssignmentsBySourceNodeId,
      (targetNodeId, sourceNodeId) => ({sourceNodeId, targetNodeId}),
      missingEdgeSpecs,
      targetsBySourceNodeId,
      numEdgesBySourceNodeId,
      numEdgesByTargetNodeId
    );
  } else {
    const numAssignmentsByTargetNodeId = getBalancedAssignmentCountsByNodeId(
      uncoveredSourceNodeIds,
      eligibleTargetNodeIds,
      uncoveredTargetNodeIds,
      numEdgesByTargetNodeId,
      sourcePositionsByNodeId,
      targetPositionsByNodeId
    );
    recordOrderedAssignments(
      uncoveredSourceNodeIds,
      eligibleTargetNodeIds,
      numAssignmentsByTargetNodeId,
      (sourceNodeId, targetNodeId) => ({sourceNodeId, targetNodeId}),
      missingEdgeSpecs,
      targetsBySourceNodeId,
      numEdgesBySourceNodeId,
      numEdgesByTargetNodeId
    );
  }
  return missingEdgeSpecs;
}

/**
 * Returns the minimum missing edges needed to cover eligible nodes across direct method links.
 *
 * Node arrays must follow rendered vertical order. Existing edges count toward coverage and degree,
 * including edges whose endpoints are no longer eligible for a newly generated edge.
 */
export function getMissingReplicateFlowchartEdgeSpecs({
  directMethodLinks,
  nodesByMethodId,
  existingEdgeSpecs,
}: GetMissingReplicateFlowchartEdgeSpecsArgs): Array<FlowchartEdgeSpec> {
  const targetsBySourceNodeId = buildAdjacencyMap(existingEdgeSpecs);
  const missingEdgeSpecs: Array<FlowchartEdgeSpec> = [];
  directMethodLinks.forEach(({sourceMethodId, targetMethodId}) => {
    const sourceNodes = nodesByMethodId.get(sourceMethodId) ?? [];
    const targetNodes = nodesByMethodId.get(targetMethodId) ?? [];
    missingEdgeSpecs.push(
      ...getMissingEdgeSpecsForMethodPair(sourceNodes, targetNodes, targetsBySourceNodeId)
    );
  });

  return missingEdgeSpecs;
}
